import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, Save, UserCircle, Palette, Sparkles, RefreshCw, Check, X, Plus, Trash2, GripVertical } from "lucide-react";
import { DEFAULT_BRAND } from "@/lib/brand";

const BRAND_FIELDS = [
  { key: "primary", label: "Primary (darkest)" },
  { key: "secondary", label: "Secondary" },
  { key: "accent", label: "Complementary accent" },
  { key: "background", label: "Background" },
];

const SOCIAL_LABELS = {
  podcast_page: "Podcast page",
  apple_podcasts: "Apple Podcasts (show page)",
  spotify: "Spotify (show page)",
};
const SOCIAL_HINTS = {
  apple_podcasts: "Guests get a 'leave a review' button on their promo kit",
  spotify: "Guests get a 'rate on Spotify' button on their promo kit",
};

export default function ShowSettings() {
  const { user } = useAuth();
  const [show, setShow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [timezones, setTimezones] = useState(["America/New_York"]);

  const load = useCallback(async () => {
    const { data } = await api.get("/shows/me");
    setShow(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/config").then(r => {
      if (Array.isArray(r.data?.supported_timezones) && r.data.supported_timezones.length) {
        setTimezones(r.data.supported_timezones);
      }
    }).catch((e) => console.warn("Config load failed:", e));
  }, []);
  if (!show) return <div>Loading...</div>;

  const F = (key, label, multi = false, placeholder = "") => (
    <div>
      <label className="pgm-label">{label}</label>
      {multi
        ? <textarea className="pgm-textarea" placeholder={placeholder} value={show[key] || ""} onChange={(e) => setShow({ ...show, [key]: e.target.value })} data-testid={`setting-${key}`} />
        : <input className="pgm-input" placeholder={placeholder} value={show[key] || ""} onChange={(e) => setShow({ ...show, [key]: e.target.value })} data-testid={`setting-${key}`} />}
    </div>
  );

  const updateSocial = (k, v) => setShow({ ...show, socials: { ...(show.socials || {}), [k]: v } });

  const setTitle = (i, v) => {
    const arr = [...(show.example_titles || ["", "", ""])];
    while (arr.length < 3) arr.push("");
    arr[i] = v;
    setShow({ ...show, example_titles: arr.slice(0, 3) });
  };

  const save = async () => {
    setSaving(true);
    const titles = [...(show.example_titles || ["", "", ""])];
    while (titles.length < 3) titles.push("");
    const { data } = await api.put("/shows/me", {
      show_name: show.show_name,
      show_description: show.show_description,
      episode_prefix: show.episode_prefix,
      intake_form_url: show.intake_form_url,
      reply_to_email: show.reply_to_email || "",
      use_builtin_intake: show.use_builtin_intake !== false,
      broadcast_link: show.broadcast_link,
      socials: show.socials,
      brand_colors: show.brand_colors,
      podcast_logo_url: show.podcast_logo_url,
      host_image_url: show.host_image_url,
      audience: show.audience || "",
      viewer_takeaway: show.viewer_takeaway || "",
      host_bio: show.host_bio || "",
      voice_tone: show.voice_tone || "",
      never_words: show.never_words || "",
      example_titles: titles.slice(0, 3),
      default_cta: show.default_cta || "",
      show_format: show.show_format || "live_video",
      allow_emojis: !!show.allow_emojis,
      studio_link_reminder_days: Array.isArray(show.studio_link_reminder_days) && show.studio_link_reminder_days.length > 0
        ? show.studio_link_reminder_days
        : [7, 3],
      intake_welcome: show.intake_welcome || "",
      intake_questions: show.intake_questions || [],
    });
    setShow(data); setSaving(false); setStatus("Saved!"); setTimeout(() => setStatus(""), 1800);
  };

  return (
    <div className="pgm-reveal">
      <div className="pgm-micro">◆ Configuration</div>
      <h1 className="pgm-display" style={{ fontSize: 44 }}>Show Settings</h1>
      <p style={{ color: "var(--pgm-wine)", opacity: 0.75, marginBottom: 22 }}>
        Show name, description, intake form, podcast logo, host image — plus your account details. All scoped to your show.
      </p>

      <AccountSection />

      <EmailHealthSection />

      <h2 className="pgm-display" style={{ fontSize: 22, marginTop: 18 }}>◆ Show images</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 8 }}>
        <ImageUploader label="Podcast logo (1400×1400 PNG/JPG)" value={show.podcast_logo_url} onChange={(url) => setShow({ ...show, podcast_logo_url: url })} testid="upload-logo" />
        <ImageUploader label="Host image (min 800×800)" value={show.host_image_url} onChange={(url) => setShow({ ...show, host_image_url: url })} testid="upload-host" />
      </div>

      <h2 className="pgm-display" style={{ fontSize: 22, marginTop: 24 }}>◆ Guest intake form</h2>
      <div className="pgm-card" style={{ marginTop: 8 }} data-testid="intake-mode-card">
        <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={show.use_builtin_intake !== false}
            onChange={(e) => setShow({ ...show, use_builtin_intake: e.target.checked })}
            style={{ marginTop: 3, width: 16, height: 16, accentColor: "var(--pgm-magenta)" }}
            data-testid="setting-builtin-intake"
          />
          <div>
            <strong>Use the built-in intake form (recommended)</strong>
            <p style={{ fontSize: 13, opacity: 0.75, margin: "4px 0 0" }}>
              Each guest gets their own secure link — it auto-fills their profile when submitted, checks "Form complete" on the checklist, and stops the 5-day auto-reminders. The <code>{"{intake_form_url}"}</code> placeholder in your emails automatically uses each guest's personal link. Copy a guest's link from their detail page.
            </p>
          </div>
        </label>
      </div>

      <h2 className="pgm-display" style={{ fontSize: 22, marginTop: 24 }}>◆ Show details</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 8 }}>
        {F("show_name", "Show name")}
        {F("episode_prefix", "Episode prefix", false, "e.g. TSL → TSL001")}
        <div style={{ gridColumn: "1 / -1" }}>{F("show_description", "Show description (fed into AI promo prompts)", true)}</div>
        {show.use_builtin_intake === false && (
          <div style={{ gridColumn: "1 / -1" }}>{F("intake_form_url", "External intake form URL (Google Forms or equivalent)")}</div>
        )}
        <div style={{ gridColumn: "1 / -1" }}>{F("broadcast_link", "Default join link (StreamYard / Zoom room you usually use)")}</div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="pgm-label" htmlFor="reply_to_email">Reply-to email (optional)</label>
          <p style={{ fontSize: 12, opacity: 0.75, margin: "0 0 8px 0" }}>
            Guest emails go out as "{user?.name || "Your name"} via ShowRunnerHQ". When a guest replies, the reply lands here.
            Leave blank and we'll use your account email ({user?.email || "your login email"}) — set it to a team or booking inbox if you'd rather replies went there.
          </p>
          <input
            id="reply_to_email"
            className="pgm-input"
            type="email"
            placeholder="bookings@yourshow.com"
            value={show.reply_to_email || ""}
            onChange={(e) => setShow({ ...show, reply_to_email: e.target.value })}
            data-testid="setting-reply-to-email"
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="pgm-label" htmlFor="host_tz">Show timezone</label>
          <p style={{ fontSize: 12, opacity: 0.75, margin: "0 0 8px 0" }}>
            Used to fire the day-of reminder at 8 AM local on the recording day, and to render recording times in guest emails.
          </p>
          <select
            id="host_tz"
            className="pgm-input"
            value={show.host_tz || "America/New_York"}
            onChange={(e) => setShow({ ...show, host_tz: e.target.value })}
            data-testid="host-tz-select"
            style={{ maxWidth: 400 }}
          >
            {timezones.map((tz) => (
              <option key={tz} value={tz}>{tz.replace("_", " ")}</option>
            ))}
          </select>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="pgm-label">Studio link reminder cadence</label>
          <p style={{ fontSize: 12, opacity: 0.75, margin: "0 0 8px 0" }}>
            When a guest is booked but has no broadcast link yet, we'll nudge you by email at each selected day-out. The pre-show check-in to the guest is held until you set the link on that guest's Info tab.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="cadence-chips">
            {[5, 4, 3].map((d) => {
              const current = Array.isArray(show.studio_link_reminder_days) ? show.studio_link_reminder_days : [5, 3];
              const on = current.includes(d);
              const toggle = () => {
                const next = on ? current.filter((x) => x !== d) : [...current, d].sort((a, b) => b - a);
                setShow({ ...show, studio_link_reminder_days: next });
              };
              return (
                <button
                  key={d}
                  type="button"
                  onClick={toggle}
                  data-testid={`cadence-chip-${d}`}
                  aria-pressed={on}
                  style={{
                    padding: "8px 16px",
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    background: on ? "var(--pgm-olive)" : "transparent",
                    color: on ? "var(--pgm-cream)" : "var(--pgm-ink)",
                    border: `2px solid ${on ? "var(--pgm-olive)" : "var(--pgm-rule)"}`,
                    cursor: "pointer",
                    borderRadius: 0,
                    transition: "all 0.15s",
                  }}
                >
                  {d} day{d === 1 ? "" : "s"} out
                </button>
              );
            })}
          </div>
          {(!Array.isArray(show.studio_link_reminder_days) || show.studio_link_reminder_days.length === 0) && (
            <div style={{ fontSize: 12, color: "var(--pgm-orange)", marginTop: 6 }}>
              Pick at least one — we'll fall back to 5 &amp; 3 days if none are selected.
            </div>
          )}
        </div>
      </div>

      <h2 className="pgm-display" style={{ fontSize: 22, marginTop: 24 }}>◆ AI Copy Settings</h2>
      <p style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>
        These fields are sent into the AI prompt every time promo copy is generated for your show. The better you fill these in, the more the AI sounds like you.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 10 }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="pgm-label">Who is your audience?</label>
          <textarea
            className="pgm-textarea"
            value={show.audience || ""}
            onChange={(e) => setShow({ ...show, audience: e.target.value })}
            data-testid="setting-audience"
          />
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
            Describe your audience in 1&ndash;2 sentences: who they are, what they want, what they struggle with. Example: &ldquo;New parents of twins who are exhausted and want practical routines that actually work.&rdquo;
          </div>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <label className="pgm-label">What does your audience get from every episode?</label>
          <input
            className="pgm-input"
            value={show.viewer_takeaway || ""}
            onChange={(e) => setShow({ ...show, viewer_takeaway: e.target.value })}
            data-testid="setting-viewer_takeaway"
          />
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
            Your show&rsquo;s promise in one sentence. Example: &ldquo;Honest stories and practical tools from people who&rsquo;ve been through it.&rdquo;
          </div>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <label className="pgm-label">Host bio for promo copy</label>
          <textarea
            className="pgm-textarea"
            value={show.host_bio || ""}
            onChange={(e) => setShow({ ...show, host_bio: e.target.value })}
            data-testid="setting-host_bio"
          />
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
            2&ndash;3 sentences about you in the third person: your background, why you&rsquo;re credible on this topic, and who the show is for. This is how promo copy introduces you.
          </div>
        </div>

        <div>
          <label className="pgm-label">Voice: 3 tone words</label>
          <input
            className="pgm-input"
            value={show.voice_tone || ""}
            onChange={(e) => setShow({ ...show, voice_tone: e.target.value })}
            data-testid="setting-voice_tone"
          />
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
            Three words that describe how your show should sound. Examples: &ldquo;Warm, direct, grounded&rdquo; or &ldquo;Playful, curious, bold&rdquo;.
          </div>
        </div>

        <div>
          <label className="pgm-label">Show format <span style={{ color: "var(--pgm-orange)" }}>*</span></label>
          <select
            className="pgm-input"
            value={show.show_format || "live_video"}
            onChange={(e) => setShow({ ...show, show_format: e.target.value })}
            data-testid="setting-show_format"
          >
            <option value="live_video">Live video show</option>
            <option value="recorded_video">Recorded video show</option>
            <option value="audio_podcast">Audio podcast</option>
          </select>
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
            Drives whether promo copy says &ldquo;watch live&rdquo;, &ldquo;watch&rdquo;, or &ldquo;listen&rdquo;.
          </div>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <label className="pgm-label">Words and phrases to never use</label>
          <textarea
            className="pgm-textarea"
            style={{ minHeight: 140 }}
            placeholder="One per line"
            value={show.never_words || ""}
            onChange={(e) => setShow({ ...show, never_words: e.target.value })}
            data-testid="setting-never_words"
          />
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
            The AI will never use anything on this list. One per line.
          </div>
          <label style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={!!show.allow_emojis}
              onChange={(e) => setShow({ ...show, allow_emojis: e.target.checked })}
              style={{ width: 16, height: 16, accentColor: "var(--pgm-magenta)" }}
              data-testid="setting-allow_emojis"
            />
            <span style={{ fontSize: 13 }}>Allow emojis in promo copy</span>
          </label>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <label className="pgm-label">3 example titles you love</label>
          {[0, 1, 2].map(i => (
            <input
              key={i}
              className="pgm-input"
              style={{ marginBottom: 8 }}
              placeholder={`Example title ${i + 1}`}
              value={(show.example_titles || [])[i] || ""}
              onChange={(e) => setTitle(i, e.target.value)}
              data-testid={`setting-example-title-${i}`}
            />
          ))}
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
            Titles from your show or any show whose style you want the AI to match.
          </div>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <label className="pgm-label">Default call to action</label>
          <input
            className="pgm-input"
            value={show.default_cta || ""}
            onChange={(e) => setShow({ ...show, default_cta: e.target.value })}
            data-testid="setting-default_cta"
          />
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
            What should promo copy ask people to do? Example: &ldquo;Subscribe on YouTube and join our community group.&rdquo;
          </div>
        </div>
      </div>

      <IntakeWordingSection show={show} setShow={setShow} />

      <h2 className="pgm-display" style={{ fontSize: 22, marginTop: 24, display: "flex", alignItems: "center", gap: 8 }}><Palette size={18} /> ◆ Brand colors</h2>
      <p style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>
        Your show's colors on the guest intake form, promo kit pages, and emails. The ShowRunner HQ app itself keeps its own look.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 10 }}>
        {BRAND_FIELDS.map(({ key, label }) => {
          const val = (show.brand_colors || {})[key] || DEFAULT_BRAND[key];
          const set = (v) => setShow({ ...show, brand_colors: { ...DEFAULT_BRAND, ...(show.brand_colors || {}), [key]: v } });
          return (
            <div key={key}>
              <label className="pgm-label">{label}</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="color" value={val} onChange={(e) => set(e.target.value)} style={{ width: 44, height: 38, padding: 2, border: "1.5px solid var(--pgm-rule)", background: "var(--pgm-paper)", cursor: "pointer" }} data-testid={`brand-picker-${key}`} />
                <input className="pgm-input" value={val} onChange={(e) => set(e.target.value)} style={{ flex: 1 }} data-testid={`brand-hex-${key}`} />
              </div>
            </div>
          );
        })}
      </div>

      <h2 className="pgm-display" style={{ fontSize: 22, marginTop: 24 }}>◆ Social links</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 8 }}>
        {["linkedin", "youtube", "facebook", "instagram", "podcast_page", "website", "apple_podcasts", "spotify"].map(k => (
          <div key={k}>
            <label className="pgm-label">{SOCIAL_LABELS[k] || k.replace("_", " ")}</label>
            <input className="pgm-input" placeholder={SOCIAL_HINTS[k] || ""} value={(show.socials || {})[k] || ""} onChange={(e) => updateSocial(k, e.target.value)} data-testid={`social-${k}`} />
          </div>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginTop: 22 }}>
        {status && <span className="pgm-badge pgm-badge-olive">{status}</span>}
        <button className="pgm-btn" disabled={saving} onClick={save} data-testid="save-settings"><Save size={14} /> {saving ? "Saving..." : "Save settings"}</button>
      </div>

      <IntakeFormReference formUrl={show.intake_form_url} builtin={show.use_builtin_intake !== false} />
    </div>
  );
}

const DEFAULT_INTAKE_QUESTIONS = [
  { id: "q1", label: "What's one thing most people get wrong about your topic or experience?" },
  { id: "q2", label: "Share one specific moment, number, or before-and-after from your experience." },
];

function HealthRow({ label, state, detail, testid }) {
  const tone = state === "ok" ? "var(--pgm-olive)" : state === "warn" ? "var(--pgm-orange)" : state === "bad" ? "#B3261E" : "var(--pgm-rule)";
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid var(--pgm-rule)" }} data-testid={testid}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: tone, marginTop: 5, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--pgm-wine)" }}>{label}</div>
        <div style={{ fontSize: 12.5, opacity: 0.8, lineHeight: 1.5, wordBreak: "break-word" }}>{detail}</div>
      </div>
    </div>
  );
}

function EmailHealthSection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.get("/email/health");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't run the health check");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const when = (iso) => {
    if (!iso) return "never";
    const d = new Date(iso);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    const rel = mins < 1 ? "just now" : mins < 60 ? `${mins} min ago` : mins < 1440 ? `${Math.round(mins / 60)} h ago` : `${Math.round(mins / 1440)} d ago`;
    return `${d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} (${rel})`;
  };

  const provider = data?.provider || {};
  const domain = data?.domain || {};
  const sched = data?.scheduler || {};
  const allGood = data && data.configured && provider.reachable && domain.verified && sched.healthy;
  const recentFailure = !!data?.last_failure?.at &&
    (Date.now() - new Date(data.last_failure.at).getTime()) < 7 * 24 * 3600 * 1000;

  return (
    <>
      <h2 className="pgm-display" style={{ fontSize: 22, marginTop: 24 }}>◆ Email health</h2>
      <div className="pgm-card" style={{ marginTop: 8, borderLeftColor: allGood ? "var(--pgm-olive)" : "var(--pgm-orange)" }} data-testid="email-health-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
          <div>
            <strong data-testid="email-health-headline">
              {busy ? "Checking…" : error ? "Health check failed" : allGood ? "Sending is healthy" : "Needs attention"}
            </strong>
            <p style={{ fontSize: 12.5, opacity: 0.75, margin: "3px 0 0" }}>
              Live check of Resend, your sending domain, the scheduler and your most recent send.
            </p>
          </div>
          <button className="pgm-btn pgm-btn-ghost" onClick={load} disabled={busy} data-testid="email-health-refresh">
            <RefreshCw size={13} /> {busy ? "Checking…" : "Re-check"}
          </button>
        </div>

        {error && <div style={{ color: "var(--pgm-orange)", fontSize: 13 }} data-testid="email-health-error">{error}</div>}

        {data && (
          <div style={{ marginTop: 6 }}>
            <HealthRow
              testid="health-key"
              label="API key"
              state={data.configured ? "ok" : "bad"}
              detail={data.configured
                ? `Set in this environment. Sending as ${data.from_address}.`
                : "RESEND_API_KEY is missing here, so nothing can send. Add it in the deployment Secrets tab and republish."}
            />
            <HealthRow
              testid="health-provider"
              label="Resend reachable"
              state={provider.reachable ? "ok" : "bad"}
              detail={provider.reachable
                ? `Responded ${provider.status} in ${provider.latency_ms} ms.`
                : (provider.error || "No response from Resend.")}
            />
            <HealthRow
              testid="health-domain"
              label={`Sending domain — ${domain.name || "unknown"}`}
              state={domain.verified ? "ok" : provider.reachable ? "bad" : "idle"}
              detail={domain.verified
                ? `Verified${domain.region ? ` (${domain.region})` : ""}. Emails send from your own domain.`
                : provider.reachable
                ? `Status: ${domain.status || "unknown"}. Until this is verified in Resend, your emails will bounce or be rejected.`
                : "Couldn't check — Resend didn't respond."}
            />
            <HealthRow
              testid="health-scheduler"
              label="Automation scheduler"
              state={sched.healthy ? "ok" : "warn"}
              detail={sched.last_run_at
                ? `Last pass ${when(sched.last_run_at)} · runs every ${sched.interval_minutes} minutes.`
                : `No pass recorded yet — it runs every ${sched.interval_minutes} minutes and records the first one shortly after a restart.`}
            />
            <HealthRow
              testid="health-last-success"
              label="Last successful send"
              state={data.last_success ? "ok" : "warn"}
              detail={data.last_success
                ? `${data.last_success.template_label} to ${data.last_success.to} — ${when(data.last_success.at)}`
                : "No successful send recorded for this show yet."}
            />
            <HealthRow
              testid="health-last-failure"
              label="Last failed send"
              state={data.last_failure ? (recentFailure ? "bad" : "idle") : "ok"}
              detail={data.last_failure
                ? `${data.last_failure.template_label} to ${data.last_failure.to} — ${when(data.last_failure.at)}${recentFailure ? "" : " (nothing has failed since)"}. Reason: ${(data.last_failure.reason || "unknown").slice(0, 180)}`
                : "No failures recorded. Every send has been accepted by Resend."}
            />
            <HealthRow
              testid="health-volume"
              label="Last 7 days"
              state={(data.last_7_days?.failed || 0) > 0 ? "warn" : "ok"}
              detail={`${data.last_7_days?.sent || 0} sent · ${data.last_7_days?.failed || 0} failed`}
            />
            <HealthRow
              testid="health-delivery"
              label="Delivery (last 7 days)"
              state={(data.last_7_days?.bounced || 0) > 0 ? "bad" : (data.last_7_days?.delivered || 0) > 0 ? "ok" : "idle"}
              detail={`${data.last_7_days?.delivered || 0} confirmed delivered · ${data.last_7_days?.bounced || 0} bounced or marked spam · ${data.last_7_days?.pending || 0} still awaiting confirmation.${(data.bouncing_guests || 0) > 0 ? ` ${data.bouncing_guests} guest${data.bouncing_guests === 1 ? "" : "s"} currently have a bouncing address — their automated emails are paused.` : ""}`}
            />
            <HealthRow
              testid="health-webhook"
              label="Real-time delivery webhook"
              state={data.webhook?.configured ? (data.webhook?.last_event_at ? "ok" : "warn") : "warn"}
              detail={data.webhook?.configured
                ? (data.webhook.last_event_at
                  ? `Verified and live — last event (${data.webhook.last_event_type || "unknown"}) ${when(data.webhook.last_event_at)}.`
                  : "Secret is set, but Resend hasn't pushed an event yet. Add the endpoint below in Resend → Webhooks if you haven't.")
                : `Not set up — status still arrives via the 10-minute poll, just slower. Add ${data.webhook?.endpoint} in Resend → Webhooks and paste the signing secret as RESEND_WEBHOOK_SECRET.`}
            />
            <HealthRow
              testid="health-links"
              label="Links inside emails point to"
              state={data.links_base_url ? "ok" : "warn"}
              detail={data.links_base_url
                ? data.links_base_url
                : "FRONTEND_URL isn't set, so intake and confirm links will be missing from your emails."}
            />
            <p style={{ fontSize: 11.5, opacity: 0.65, marginTop: 10, marginBottom: 0 }}>
              Checked {when(data.checked_at)}. The key is read fresh on every send, but it's only loaded into the running app at startup — after changing the secret in production, republish for it to take effect.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

function withDefaultsIfEmpty(list) {
  if (Array.isArray(list) && list.length > 0) return list;
  return DEFAULT_INTAKE_QUESTIONS.map(q => ({ ...q }));
}

function makeId() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `q-${Math.random().toString(36).slice(2, 10)}`;
}

function IntakeWordingSection({ show, setShow }) {
  const [drafts, setDrafts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const questions = withDefaultsIfEmpty(show.intake_questions);

  const setQuestions = (next) => setShow({ ...show, intake_questions: next });

  const updateLabel = (idx, label) => {
    const next = questions.map((q, i) => (i === idx ? { ...q, label } : q));
    setQuestions(next);
  };
  const addQuestion = () => setQuestions([...questions, { id: makeId(), label: "" }]);
  const removeQuestion = (idx) => {
    if (!window.confirm("Remove this question? Existing guest answers for it will stay in the database but won't be shown on the intake form or Info tab.")) return;
    setQuestions(questions.filter((_, i) => i !== idx));
  };
  const moveQuestion = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[idx], next[j]] = [next[j], next[idx]];
    setQuestions(next);
  };

  const generate = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/shows/me/intake-suggest");
      // Convert 2-question AI response into full drafts payload (welcome + editable list)
      setDrafts({
        welcome: data.welcome || "",
        questions: [
          { id: "q1", label: data.q1 || DEFAULT_INTAKE_QUESTIONS[0].label },
          { id: "q2", label: data.q2 || DEFAULT_INTAKE_QUESTIONS[1].label },
        ],
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "AI drafting failed — please try again");
    } finally { setLoading(false); }
  };

  const applyDrafts = async () => {
    setLoading(true);
    try {
      const { data } = await api.put("/shows/me/intake-wording", drafts);
      setShow({ ...show, ...data });
      setDrafts(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setLoading(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset the intake welcome and questions back to the platform defaults? Any custom questions you added will be removed.")) return;
    setLoading(true);
    try {
      const { data } = await api.put("/shows/me/intake-wording", {
        welcome: "",
        questions: DEFAULT_INTAKE_QUESTIONS.map(q => ({ ...q })),
      });
      setShow({ ...show, ...data });
    } finally { setLoading(false); }
  };

  return (
    <div style={{ marginTop: 28 }} data-testid="intake-wording-section">
      <h2 className="pgm-display" style={{ fontSize: 22, marginTop: 24 }}>◆ Intake form wording</h2>
      <p style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>
        A welcome message and the short story questions on your guest intake form. Edit any wording, add your own questions, or let AI draft the first two tuned to your show. You control the audience takeaway — the guest doesn&rsquo;t decide that.
      </p>

      <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
        <button className="pgm-btn pgm-btn-olive" onClick={generate} disabled={loading} data-testid="intake-suggest-btn">
          <Sparkles size={13} /> {loading && !drafts ? "Drafting…" : "Draft Q1 & Q2 with AI"}
        </button>
        <button className="pgm-btn pgm-btn-ghost" onClick={resetDefaults} disabled={loading} data-testid="intake-reset-defaults">
          <RefreshCw size={12} /> Reset to defaults
        </button>
      </div>
      {err && <div className="pgm-callout" style={{ borderLeftColor: "var(--pgm-orange)", marginTop: 12 }} data-testid="intake-suggest-error">{err}</div>}

      {drafts && (
        <div className="pgm-card" style={{ marginTop: 14, borderLeftColor: "var(--pgm-magenta)" }} data-testid="intake-drafts-panel">
          <div className="pgm-micro">◆ AI Drafts &mdash; edit before applying</div>
          <p style={{ fontSize: 12, opacity: 0.7, margin: "4px 0 12px" }}>Applies to the two default questions. Any extra questions you added stay untouched until you click Apply below.</p>
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <label className="pgm-label">Welcome message</label>
              <textarea className="pgm-textarea" value={drafts.welcome} onChange={(e) => setDrafts({ ...drafts, welcome: e.target.value })} data-testid="draft-welcome" />
            </div>
            {drafts.questions.map((q, i) => (
              <div key={q.id}>
                <label className="pgm-label">Question {i + 1}</label>
                <input
                  className="pgm-input"
                  value={q.label}
                  onChange={(e) => setDrafts({ ...drafts, questions: drafts.questions.map((qq, j) => j === i ? { ...qq, label: e.target.value } : qq) })}
                  data-testid={`draft-q${i + 1}`}
                />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button className="pgm-btn" onClick={applyDrafts} disabled={loading} data-testid="draft-apply">
              <Check size={13} /> Apply to intake form
            </button>
            <button className="pgm-btn pgm-btn-ghost" onClick={() => setDrafts(null)} disabled={loading} data-testid="draft-discard">
              <X size={13} /> Keep current
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: 14, marginTop: 20 }}>
        <div>
          <label className="pgm-label">Welcome message (shown at the top of the intake form)</label>
          <textarea
            className="pgm-textarea"
            placeholder="Leave blank for no welcome message"
            value={show.intake_welcome || ""}
            onChange={(e) => setShow({ ...show, intake_welcome: e.target.value })}
            data-testid="setting-intake_welcome"
          />
        </div>

        <div>
          <label className="pgm-label" style={{ display: "block", marginBottom: 8 }}>Story questions</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="questions-list">
            {questions.map((q, i) => (
              <div key={q.id || i} data-testid={`question-row-${i}`} style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "var(--pgm-cream)", padding: 10, borderLeft: "2px solid var(--pgm-olive)" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 6 }}>
                  <button type="button" className="pgm-btn pgm-btn-ghost" style={{ padding: "2px 6px", fontSize: 10 }} onClick={() => moveQuestion(i, -1)} disabled={i === 0} title="Move up" data-testid={`question-up-${i}`}>▲</button>
                  <button type="button" className="pgm-btn pgm-btn-ghost" style={{ padding: "2px 6px", fontSize: 10 }} onClick={() => moveQuestion(i, +1)} disabled={i === questions.length - 1} title="Move down" data-testid={`question-down-${i}`}>▼</button>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4, letterSpacing: "0.05em", textTransform: "uppercase" }}>Question {i + 1}{q.id === "q1" ? " (opinion)" : q.id === "q2" ? " (specific moment)" : ""}</div>
                  <input
                    className="pgm-input"
                    value={q.label}
                    placeholder="Type your question…"
                    onChange={(e) => updateLabel(i, e.target.value)}
                    data-testid={`question-label-${i}`}
                  />
                </div>
                <button
                  type="button"
                  className="pgm-btn pgm-btn-ghost"
                  style={{ padding: "6px 8px", color: "var(--pgm-orange)", borderColor: "var(--pgm-orange)" }}
                  onClick={() => removeQuestion(i)}
                  disabled={questions.length <= 1}
                  title={questions.length <= 1 ? "At least one question is required" : "Remove question"}
                  data-testid={`question-remove-${i}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" className="pgm-btn pgm-btn-ghost" onClick={addQuestion} style={{ marginTop: 10 }} data-testid="question-add">
            <Plus size={12} /> Add question
          </button>
        </div>

        <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4, padding: "10px 14px", background: "var(--pgm-cream)", borderLeft: "3px solid var(--pgm-olive)" }}>
          <strong>Note:</strong> The audience takeaway is your editorial call, not the guest&rsquo;s. Set it per episode on each guest&rsquo;s Info tab under &ldquo;What YOU want the audience to walk away with&rdquo;.
        </div>
      </div>
    </div>
  );
}

function IntakeFormReference({ formUrl, builtin }) {
  if (builtin) return null;
  const REQUIRED = [
    { n: 1, label: "Full name", type: "Short answer" },
    { n: 2, label: "Email address", type: "Short answer" },
    { n: 3, label: "Professional title / role", type: "Short answer" },
    { n: 4, label: "Short bio — 2–3 sentences, 3rd person", type: "Paragraph" },
    { n: 5, label: "Headshot photo — min 800×800 square", type: "File upload" },
    { n: 6, label: "Website URL", type: "Short answer" },
    { n: 7, label: "LinkedIn profile URL", type: "Short answer" },
    { n: 8, label: "3–4 questions or topics — one per line", type: "Paragraph" },
  ];
  const OPTIONAL = [
    { n: 9, label: "Facebook URL", type: "Short answer" },
    { n: 10, label: "Instagram handle", type: "Short answer" },
    { n: 11, label: "YouTube channel URL", type: "Short answer" },
    { n: 12, label: "Lead magnet title", type: "Short answer" },
    { n: 13, label: "Lead magnet URL", type: "Short answer" },
    { n: 14, label: "Anything else", type: "Paragraph" },
  ];
  return (
    <div style={{ marginTop: 40 }}>
      <h2 className="pgm-display" style={{ fontSize: 22 }}>◆ Intake form field reference</h2>
      <p style={{ fontSize: 13, color: "var(--pgm-wine)", opacity: 0.75, marginBottom: 12 }}>
        Use this when setting up your Google Form (or equivalent). All 8 required fields block submission; the 6 optional fields don't.
        {formUrl && <> Your current form URL: <a href={formUrl} target="_blank" rel="noreferrer" className="pgm-link">{formUrl}</a></>}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-magenta)" }} data-testid="intake-required">
          <div className="pgm-micro">◆ Required (8)</div>
          <ol style={{ margin: "10px 0 0 0", padding: 0, listStyle: "none" }}>
            {REQUIRED.map(f => (
              <li key={f.n} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--pgm-rule)", fontSize: 13 }}>
                <span><strong>{f.n}.</strong> {f.label}</span>
                <span className="pgm-badge pgm-badge-magenta" style={{ fontSize: 9 }}>{f.type}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-olive)" }} data-testid="intake-optional">
          <div className="pgm-micro">◆ Optional (6)</div>
          <ol style={{ margin: "10px 0 0 0", padding: 0, listStyle: "none" }}>
            {OPTIONAL.map(f => (
              <li key={f.n} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--pgm-rule)", fontSize: 13 }}>
                <span><strong>{f.n}.</strong> {f.label}</span>
                <span className="pgm-badge pgm-badge-olive" style={{ fontSize: 9 }}>{f.type}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

function ImageUploader({ label, value, onChange, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const src = value?.startsWith("/api") ? `${BACKEND_URL}${value}` : value;
  const onPick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const form = new FormData(); form.append("file", file); form.append("kind", "image");
    try {
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (err) { alert("Upload failed: " + (err?.response?.data?.detail || err.message)); }
    finally { setUploading(false); }
  };
  return (
    <div className="pgm-card">
      <label className="pgm-label">{label}</label>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <div style={{ width: 100, height: 100, border: "1.5px solid var(--pgm-rule)", background: "var(--pgm-cream)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
          {src ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 10, opacity: 0.5 }}>No image</span>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPick} style={{ display: "none" }} data-testid={testid + "-input"} />
          <button className="pgm-btn pgm-btn-ghost" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={testid}>
            <Upload size={12} /> {uploading ? "Uploading..." : (value ? "Replace" : "Upload")}
          </button>
          {value && <button className="pgm-btn pgm-btn-ghost" style={{ borderColor: "var(--pgm-orange)", color: "var(--pgm-orange)" }} onClick={() => onChange("")} data-testid={testid + "-remove"}>Remove</button>}
        </div>
      </div>
    </div>
  );
}

function AccountSection() {
  const { refreshMe } = useAuth();
  const [me, setMe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    api.get("/auth/me").then(r => setMe(r.data)).catch((e) => console.warn("Account load failed:", e));
  }, []);
  if (!me) return null;

  const set = (k, v) => setMe({ ...me, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/users/me", {
        name: me.name,
        contact_email: me.contact_email || undefined,
        contact_phone: me.contact_phone || "",
      });
      setMe(data);
      await refreshMe();
      setStatus("Saved!"); setTimeout(() => setStatus(""), 1800);
    } catch (e) {
      setStatus(e?.response?.data?.detail || "Save failed");
      setTimeout(() => setStatus(""), 3000);
    } finally { setSaving(false); }
  };

  return (
    <div style={{ marginBottom: 8 }}>
      <h2 className="pgm-display" style={{ fontSize: 22 }}>◆ Your account</h2>
      <div className="pgm-card" style={{ marginTop: 8 }} data-testid="account-card">
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
          <UserCircle size={20} color="var(--pgm-magenta)" />
          <span style={{ fontSize: 13, opacity: 0.75 }}>
            Login email: <strong>{me.email}</strong> (used to sign in)
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <div>
            <label className="pgm-label">Your name</label>
            <input className="pgm-input" value={me.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="account-name" />
          </div>
          <div>
            <label className="pgm-label">Contact email</label>
            <input className="pgm-input" type="email" placeholder="Best email to reach you" value={me.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="account-contact-email" />
          </div>
          <div>
            <label className="pgm-label">Contact phone</label>
            <input className="pgm-input" placeholder="Optional" value={me.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} data-testid="account-contact-phone" />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginTop: 14 }}>
          {status && <span className={`pgm-badge ${status === "Saved!" ? "pgm-badge-olive" : "pgm-badge-orange"}`} data-testid="account-status">{status}</span>}
          <button className="pgm-btn pgm-btn-olive" disabled={saving} onClick={save} data-testid="account-save">
            <Save size={13} /> {saving ? "Saving…" : "Save account"}
          </button>
        </div>
      </div>
    </div>
  );
}
